import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { Link, useNavigate } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import PrimaryButton from '../../components/PrimaryButton/PrimaryButton';
import SecondaryButton from '../../components/SecondaryButton/SecondaryButton';
import { useAuthUser } from '../../hooks/useAuthUser';
import { encodePathSegment, requestWithApiFallback } from '../../utils/apiRequest';
import { trackEvent } from '../../utils/analytics';
import logo from '../../assets/calcium-logo.svg';
import style from './OnboardingFlow.module.css';

const API_URL = process.env.REACT_APP_API_URL;
const STORAGE_KEY = 'calcium_onboarding_personalization';

const GOAL_OPTIONS = ['Lose fat', 'Build muscle', 'Get fit', 'Performance'];
const LEVEL_OPTIONS = ['Beginner', 'Intermediate', 'Advanced'];
const DAYS_OPTIONS = ['2', '3', '4', '5', '6'];
const EQUIPMENT_OPTIONS = ['Home', 'Gym', 'Minimal'];
const DIET_OPTIONS = ['No preference', 'Balanced', 'High protein', 'Vegetarian', 'Vegan', 'Keto'];

const OnboardingPersonalization = () => {
	const { user, isLoaded } = useAuthUser();
	const navigate = useNavigate();
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [submitError, setSubmitError] = useState('');
	const [formState, setFormState] = useState(() => {
		try {
			const saved = localStorage.getItem(STORAGE_KEY);
			if (saved) return JSON.parse(saved);
		} catch { /* ignore */ }
		return {
			goal: '',
			experienceLevel: '',
			trainingDaysPerWeek: '',
			equipment: '',
			dietPreference: ''
		};
	});

	useEffect(() => {
		try {
			localStorage.setItem(STORAGE_KEY, JSON.stringify(formState));
		} catch { /* ignore */ }
	}, [formState]);

	useEffect(() => {
		if (!isLoaded || !user?.id) return;

		const saved = localStorage.getItem(STORAGE_KEY);
		let hasLocalData = false;
		if (saved) {
			try {
				const parsed = JSON.parse(saved);
				hasLocalData = Boolean(parsed.goal && parsed.experienceLevel && parsed.trainingDaysPerWeek && parsed.equipment);
				if (hasLocalData && !isSubmitting) {
					submitPersonalization(parsed);
					return;
				}
			} catch { /* ignore */ }
		}

		if (!hasLocalData) {
			let active = true;
			requestWithApiFallback(API_URL, (base) => axios.get(
				`${base}/onboarding/resume/${encodePathSegment(user.id)}`,
				{ withCredentials: true }
			)).then((res) => {
				const route = res?.data?.route;
				if (route && active) {
					navigate(route, { replace: true });
				}
			}).catch(() => {});
			return () => { active = false; };
		}
	// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoaded, user?.id]);

	const canSubmit = useMemo(() => (
		Boolean(formState.goal)
		&& Boolean(formState.experienceLevel)
		&& Boolean(formState.trainingDaysPerWeek)
		&& Boolean(formState.equipment)
	), [formState]);

	const updateField = (field, value) => {
		setFormState((prev) => ({
			...prev,
			[field]: value
		}));
	};

	const submitPersonalization = async (data) => {
		const payload = data || formState;
		const userId = user?.id;

		if (!userId) {
			navigate(`/signup?redirectTo=${encodeURIComponent('/onboarding/personalize')}`);
			return;
		}

		if (isSubmitting) return;
		setIsSubmitting(true);
		setSubmitError('');

		try {
			const response = await requestWithApiFallback(API_URL, (base) => axios.post(
				`${base}/onboarding/draft/${encodePathSegment(userId)}`,
				payload,
				{ withCredentials: true }
			));
			const routineId = response?.data?.routineId;
			if (!routineId) {
				throw new Error('Draft routine id missing');
			}
			trackEvent('onboarding_complete_personalization', {
				goal: payload.goal,
				experienceLevel: payload.experienceLevel,
				trainingDaysPerWeek: payload.trainingDaysPerWeek,
				equipment: payload.equipment
			});
			localStorage.removeItem(STORAGE_KEY);
			navigate(`/onboarding/preview/${routineId}`);
		} catch (error) {
			console.error('Failed to create onboarding draft routine:', error);
			setSubmitError('Something went wrong generating your plan. Please try again.');
		} finally {
			setIsSubmitting(false);
		}
	};

	const handleSubmit = () => {
		if (!canSubmit) return;
		submitPersonalization();
	};

	return (
		<div className={style.OnboardingPage}>
			<SEO
				title="Personalize your 30-minute plan"
				description="Answer a few quick questions so your AI coach can build a structured 30-minute plan around your schedule."
				path="/onboarding/personalize"
			/>

			<div className={style.OnboardingCard}>
				<nav className={style.PersonalizeNav}>
					<Link to="/onboarding" className={style.PersonalizeNavLogo}>
						<img src={logo} alt="Calcium" className={style.PersonalizeNavLogoImg} />
						<span className={style.PersonalizeNavLogoText}>Calcium</span>
					</Link>
				{!user && (
					<Link to={`/login?redirectTo=${encodeURIComponent('/')}`} className={style.LandingNavLink}>
						Log in
					</Link>
				)}
				</nav>

				<span className={style.StepLabel}>Personalize</span>
				<p className={style.ContextIntro}>Designed for busy schedules. Let&apos;s build your 30-minute plan.</p>

				<section className={style.Section}>
					<h2 className={style.QuestionTitle}>What is your primary goal?</h2>
					<div className={style.SelectionGrid}>
						{GOAL_OPTIONS.map((goal) => (
							<button
								key={goal}
								type="button"
								className={`${style.SelectionCard} ${formState.goal === goal ? style.SelectionCardActive : ''}`}
								onClick={() => updateField('goal', goal)}
							>
								{goal}
							</button>
						))}
					</div>
				</section>

				<section className={style.Section}>
					<h2 className={style.QuestionTitle}>Experience level</h2>
					<div className={style.SelectionGrid}>
						{LEVEL_OPTIONS.map((level) => (
							<button
								key={level}
								type="button"
								className={`${style.SelectionCard} ${formState.experienceLevel === level ? style.SelectionCardActive : ''}`}
								onClick={() => updateField('experienceLevel', level)}
							>
								{level}
							</button>
						))}
					</div>
				</section>

				<section className={style.Section}>
					<h2 className={style.QuestionTitle}>Training days per week</h2>
					<div className={style.SelectionGrid}>
						{DAYS_OPTIONS.map((day) => (
							<button
								key={day}
								type="button"
								className={`${style.SelectionCard} ${formState.trainingDaysPerWeek === day ? style.SelectionCardActive : ''}`}
								onClick={() => updateField('trainingDaysPerWeek', day)}
							>
								{day} days
							</button>
						))}
					</div>
				</section>

				<section className={style.Section}>
					<h2 className={style.QuestionTitle}>Equipment access</h2>
					<div className={style.SelectionGrid}>
						{EQUIPMENT_OPTIONS.map((equipment) => (
							<button
								key={equipment}
								type="button"
								className={`${style.SelectionCard} ${formState.equipment === equipment ? style.SelectionCardActive : ''}`}
								onClick={() => updateField('equipment', equipment)}
							>
								{equipment}
							</button>
						))}
					</div>
				</section>

				<section className={style.Section}>
					<h2 className={style.QuestionTitle}>Diet preference (optional)</h2>
					<div className={style.SelectionGrid}>
						{DIET_OPTIONS.map((dietOption) => (
							<button
								key={dietOption}
								type="button"
								className={`${style.SelectionCard} ${formState.dietPreference === dietOption ? style.SelectionCardActive : ''}`}
								onClick={() => updateField('dietPreference', dietOption)}
							>
								{dietOption}
							</button>
						))}
					</div>
				</section>

				{submitError && (
					<p style={{ color: '#dc2626', fontSize: '0.875rem', textAlign: 'center', margin: '0 0 0.5rem' }}>
						{submitError}
					</p>
				)}

				<div className={style.CTAStack}>
					<PrimaryButton
						text={isSubmitting ? 'Building your plan...' : (user ? 'Build My 30-Min Plan' : 'Continue')}
						click={handleSubmit}
						disabled={!canSubmit}
						loading={isSubmitting}
						width="100%"
					/>
					<SecondaryButton
						text="Back"
						click={() => navigate('/onboarding')}
						width="100%"
					/>
				</div>
			</div>
		</div>
	);
};

export default OnboardingPersonalization;
